import { vestResolver } from '@hookform/resolvers/vest'
import { useIfpFormContext } from '@sec-loans-ui/ifp-loans/steps/formContext'
import { FormStep } from '@sec-loans-ui/ifp-loans/steps/FormStep'
import { assertValue } from '@shared/helpers'
import { FinnishPhoneNumberInput } from '@ui-common/components/FinnishPhoneNumberInput'
import { NameAndAddressFormSection } from '@ui-common/components/NameAndAddressFormSection'
import { useWizardForm } from '@ui-common/hooks'
import { PHONE_COUNTRY_CODE } from '@ui-common/types'
import { trimPhoneNumber } from '@ui-common/utils'
import { Divider } from '@ui-components/decorative/Divider'
import { FormSectionContainer } from '@ui-components/form/FormSectionContainer'
import { FormTextInput } from '@ui-components/form/FormTextInput'
import { RegionWithLabel } from '@ui-components/form/RegionWithLabel'
import { LargeHeading } from '@ui-components/typography/headings/headingPrimitives'
import { useLayoutEffect, useMemo } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { ExpensesFormFields } from './ExpensesFormFields'
import { createFormValidator } from './formValidator'
import { OccupationFormPart } from './OccupationFormPart'
import { OtherIncomeFormPart } from './OtherIncomeFormPart'
import { ApplicantInfoFormData } from './types'

export const ApplicantInfoStep = () => {
  const intl = useIntl()
  const { formatMessage } = intl
  const { wizardData, proceed, user, currentApplicantInfoIndex } = useIfpFormContext()
  const formDefaultValues = wizardData?.applicantInfos?.[currentApplicantInfoIndex]

  // Scroll to top when this component re-mounts (currentApplicantInfoIndex updates which is used as key for the component)
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const methods = useWizardForm<ApplicantInfoFormData>({
    defaultValues: formDefaultValues
      ? {
          ...formDefaultValues,
          phoneNumber: formDefaultValues.phoneNumber?.replace(PHONE_COUNTRY_CODE, ''),
        }
      : undefined,
    resolver: vestResolver(useMemo(() => createFormValidator(formatMessage), [formatMessage])),
  })

  const {
    formState: { isSubmitting },
    handleSubmit,
  } = methods

  const onSubmit = (formData: ApplicantInfoFormData) => {
    const updatedApplicantInfos = wizardData?.applicantInfos ? [...wizardData?.applicantInfos] : []
    updatedApplicantInfos[currentApplicantInfoIndex] = {
      ...formData,
      phoneNumber: `${PHONE_COUNTRY_CODE}${trimPhoneNumber(assertValue(formData.phoneNumber, 'formData.phoneNumber'))}`,
    }
    proceed({ applicantInfos: updatedApplicantInfos })
  }

  return (
    <FormStep
      subHeader={`${formatMessage({ id: 'ifp-loans-applicant-info-step-heading' })} (${currentApplicantInfoIndex + 1})`}
      formMethods={methods}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={isSubmitting}
    >
      <FormSectionContainer>
        <RegionWithLabel ariaLabelledBy="background-info-heading">
          <LargeHeading id="background-info-heading">
            {formatMessage({ id: 'ifp-loans-background-info-heading' })}
          </LargeHeading>
          <NameAndAddressFormSection
            user={assertValue(user, 'user')}
            label={formatMessage({ id: 'common-name-and-address' })}
          />
          <FinnishPhoneNumberInput
            label={formatMessage({ id: 'common-phone-number' })}
            description={formatMessage({ id: 'ifp-loans-background-info-phone-number-description' })}
            phoneNumberField="phoneNumber"
          />
        </RegionWithLabel>
        <Divider />
        <RegionWithLabel ariaLabelledBy="income-info-heading">
          <LargeHeading id="income-info-heading">{formatMessage({ id: 'ifp-loans-income-info-heading' })}</LargeHeading>
          <IncomeFormFields />
        </RegionWithLabel>
        <Divider />
        <RegionWithLabel ariaLabelledBy="expenses-info-heading">
          <LargeHeading id="expenses-info-heading">
            {formatMessage({ id: 'ifp-loans-applicant-info-expenses-heading' })}
          </LargeHeading>
          <ExpensesFormFields />
        </RegionWithLabel>
      </FormSectionContainer>
    </FormStep>
  )
}

const IncomeFormFields = () => {
  const { formatMessage } = useIntl()
  const { register, control } = useFormContext<ApplicantInfoFormData>()
  const { errors } = useFormState({ name: ['totalSavings'], control })

  return (
    <>
      <OccupationFormPart />
      <OtherIncomeFormPart />
      <FormTextInput
        required={true}
        label={formatMessage({ id: 'ifp-loans-applicant-info-all-savings-label' })}
        description={formatMessage({ id: 'ifp-loans-applicant-info-all-saving-description' })}
        error={errors?.totalSavings?.message}
        {...register('totalSavings')}
      />
    </>
  )
}
